import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { loadConfig } from '../utils/config';
import { 
  FaLink, FaGlobe, FaEnvelope, FaComment, FaHashtag,
  FaHome, FaUser, FaVideo, FaImage, FaMusic,
  FaGamepad, FaBook, FaCalendar, FaMap, FaStar,
  FaHeart, FaShare, FaPlay, FaPodcast, FaRss,
  FaNewspaper, FaShoppingCart, FaCode, FaCloud,
  FaCamera, FaFilm, FaHeadphones, FaMoneyBill,
  FaGift, FaLightbulb, FaCog, FaTrash, FaCrown
} from 'react-icons/fa';
import { 
  SiX,
  SiFacebook,
  SiInstagram,
  SiYoutube,
  SiTwitch,
  SiTiktok,
  SiLinkedin,
  SiGithub,
  SiDiscord,
  SiReddit,
  SiPatreon,
  SiKofi,
  SiBuymeacoffee,
  SiSpotify,
  SiSoundcloud,
  SiMedium,
  SiSubstack,
  SiMastodon,
  SiThreads,
  SiWhatsapp,
  SiTelegram,
  SiSnapchat,
  SiPinterest,
  SiVimeo,
  SiDribbble,
  SiBehance,
  SiStackoverflow,
  SiDeviantart,
  SiWechat,
  SiLine
} from 'react-icons/si';

const ICON_OPTIONS = {
  // Social Media - Major Platforms
  'x': { icon: SiX, label: 'X (Twitter)' },
  'facebook': { icon: SiFacebook, label: 'Facebook' },
  'instagram': { icon: SiInstagram, label: 'Instagram' },
  'youtube': { icon: SiYoutube, label: 'YouTube' },
  'tiktok': { icon: SiTiktok, label: 'TikTok' },
  'threads': { icon: SiThreads, label: 'Threads' },
  
  // Professional & Tech
  'linkedin': { icon: SiLinkedin, label: 'LinkedIn' },
  'github': { icon: SiGithub, label: 'GitHub' },
  'stackoverflow': { icon: SiStackoverflow, label: 'Stack Overflow' },
  
  // Gaming & Community
  'twitch': { icon: SiTwitch, label: 'Twitch' },
  'discord': { icon: SiDiscord, label: 'Discord' },
  'reddit': { icon: SiReddit, label: 'Reddit' },
  
  // Messaging
  'whatsapp': { icon: SiWhatsapp, label: 'WhatsApp' },
  'telegram': { icon: SiTelegram, label: 'Telegram' },
  'snapchat': { icon: SiSnapchat, label: 'Snapchat' },
  'wechat': { icon: SiWechat, label: 'WeChat' },
  'line': { icon: SiLine, label: 'LINE' },
  
  // Creative & Design
  'pinterest': { icon: SiPinterest, label: 'Pinterest' },
  'dribbble': { icon: SiDribbble, label: 'Dribbble' },
  'behance': { icon: SiBehance, label: 'Behance' },
  'deviantart': { icon: SiDeviantart, label: 'DeviantArt' },
  'vimeo': { icon: SiVimeo, label: 'Vimeo' },
  
  // Content & Support
  'medium': { icon: SiMedium, label: 'Medium' },
  'substack': { icon: SiSubstack, label: 'Substack' },
  'mastodon': { icon: SiMastodon, label: 'Mastodon' },
  'patreon': { icon: SiPatreon, label: 'Patreon' },
  'kofi': { icon: SiKofi, label: 'Ko-fi' },
  'buymeacoffee': { icon: SiBuymeacoffee, label: 'Buy Me a Coffee' },
  'spotify': { icon: SiSpotify, label: 'Spotify' },
  'soundcloud': { icon: SiSoundcloud, label: 'SoundCloud' },
  
  // Basic Icons
  'link': { icon: FaLink, label: 'Link' },
  'globe': { icon: FaGlobe, label: 'Website' },
  'envelope': { icon: FaEnvelope, label: 'Mail' },
  'comment': { icon: FaComment, label: 'Chat' },
  'hashtag': { icon: FaHashtag, label: 'Social' },
  
  // Navigation Icons
  'home': { icon: FaHome, label: 'Home' },
  'user': { icon: FaUser, label: 'User' },
  'cog': { icon: FaCog, label: 'Settings' },
  
  // Media Icons
  'video': { icon: FaVideo, label: 'Video' },
  'image': { icon: FaImage, label: 'Image' },
  'music': { icon: FaMusic, label: 'Music' },
  'camera': { icon: FaCamera, label: 'Camera' },
  'film': { icon: FaFilm, label: 'Film' },
  'headphones': { icon: FaHeadphones, label: 'Audio' },
  
  // Content Icons
  'book': { icon: FaBook, label: 'Book' },
  'newspaper': { icon: FaNewspaper, label: 'News' },
  'calendar': { icon: FaCalendar, label: 'Calendar' },
  'map': { icon: FaMap, label: 'Map' },
  
  // Entertainment Icons
  'gamepad': { icon: FaGamepad, label: 'Game' },
  'play': { icon: FaPlay, label: 'Stream' },
  'podcast': { icon: FaPodcast, label: 'Podcast' },
  
  // Business/Shopping Icons
  'shopping-cart': { icon: FaShoppingCart, label: 'Shop' },
  'money-bill': { icon: FaMoneyBill, label: 'Money' },
  'gift': { icon: FaGift, label: 'Gift' },
  
  // Tech Icons
  'code': { icon: FaCode, label: 'Code' },
  'cloud': { icon: FaCloud, label: 'Cloud' },
  
  // Misc Icons
  'star': { icon: FaStar, label: 'Star' },
  'heart': { icon: FaHeart, label: 'Heart' },
  'share': { icon: FaShare, label: 'Share' },
  'rss': { icon: FaRss, label: 'Feed' },
  'lightbulb': { icon: FaLightbulb, label: 'Idea' }
};

const IconSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedIcon = ICON_OPTIONS[value] || ICON_OPTIONS['link'];

  return (
    <div className="relative">
      <button
        type="button"
        className="btn btn-sm btn-ghost w-10 min-h-8 h-8"
        onClick={() => setIsOpen(!isOpen)}
      >
        {React.createElement(selectedIcon.icon, {})}
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-base-100 shadow-xl rounded-box border border-base-300 p-2" 
          style={{ 
            maxHeight: '300px',
            width: '256px',
            left: '100%',
            top: '0',
            marginLeft: '0.5rem',
            overflowX: 'hidden'
          }}>
          <div className="overflow-y-auto overflow-x-hidden max-h-[280px]">
            <div className="grid grid-cols-6 gap-1" style={{ width: '100%', minWidth: '0' }}>
              {Object.entries(ICON_OPTIONS).map(([key, { icon: Icon, label }], index) => (
                <button
                  key={key}
                  className={`btn btn-ghost p-2 h-9 min-h-0 w-9 tooltip ${((index + 1) % 6 > 3 || (index + 1) % 6 === 0) ? 'tooltip-left' : 'tooltip-right'}`}
                  data-tip={label}
                  style={{ position: 'relative' }}
                  onClick={() => {
                    onChange(key);
                    setIsOpen(false);
                  }}
                >
                  <Icon />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BADGE_OPTIONS = {
  'none': { type: '', text: 'No Badge', color: 'inherit', class: '' },
  'info': { type: 'info', text: 'Info', color: '#3b82f6', class: 'badge-info' },
  'warning': { type: 'warning', text: 'Warning', color: '#f59e0b', class: 'badge-warning' },
  'error': { type: 'error', text: 'Error', color: '#ef4444', class: 'badge-error' },
  'success': { type: 'success', text: 'Success', color: '#10b981', class: 'badge-success' }
};

const BadgeSelector = ({ value = 'none', text = '', onChangeType, onChangeText, onBlurText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localText, setLocalText] = useState(text || '');
  const selectedType = BADGE_OPTIONS[value] ? value : 'none';

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    setLocalText(newValue);
    onChangeText(newValue);
  };

  const handleTextBlur = (e) => {
    const newValue = e.target.value;
    onBlurText(newValue);
  };

  const handleBadgeSelect = (key) => {
    onChangeType(key, localText);
    setIsOpen(false);
  };

  return (
    <div className="relative w-24">
      <div className="flex items-center w-full">
        <div className="relative flex-grow">
          <div className="relative">
            <input
              type="text"
              className="input input-sm input-bordered w-full pr-8"
              value={localText}
              onChange={handleTextChange}
              onBlur={handleTextBlur}
              placeholder="Badge"
              style={{ 
                color: BADGE_OPTIONS[selectedType]?.color || 'inherit',
                caretColor: 'currentColor'
              }}
            />
            <div className="absolute right-0 top-0 bottom-0 flex items-center pr-2 gap-1">
              {selectedType !== 'none' && localText && (
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: BADGE_OPTIONS[selectedType]?.color }}
                />
              )}
              <button
                type="button"
                className="z-10"
                onClick={() => setIsOpen(!isOpen)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-32 right-0 bg-base-100 shadow-xl rounded-box border border-base-300 p-2">
          <div className="flex flex-col gap-1">
            {Object.entries(BADGE_OPTIONS).map(([key, { type, text: defaultText, color }]) => (
              <button
                key={key}
                className="btn btn-sm btn-ghost justify-start h-8 min-h-0"
                onClick={() => handleBadgeSelect(key)}
              >
                <div className="flex items-center gap-2">
                  {type && (
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: color }}
                    />
                  )}
                  <span style={{ color }}>{defaultText}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SortableLink = ({ link, index, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: link.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className="card bg-base-200"
    >
      <div className="card-body p-4">
        <div className="flex gap-4">
          <div 
            {...attributes}
            {...listeners}
            className="flex items-center cursor-grab"
          >
            <svg 
              width="10" 
              height="20" 
              viewBox="0 0 10 20" 
              fill="currentColor" 
              className="opacity-40 hover:opacity-60 transition-opacity"
            >
              <circle cx="2" cy="4" r="1.5" />
              <circle cx="8" cy="4" r="1.5" />
              <circle cx="2" cy="10" r="1.5" />
              <circle cx="8" cy="10" r="1.5" />
              <circle cx="2" cy="16" r="1.5" />
              <circle cx="8" cy="16" r="1.5" />
            </svg>
          </div>
          <div className="flex-1 space-y-2">
            <input
              type="url"
              className="input input-sm input-bordered w-full"
              value={link.url}
              onChange={(e) => onUpdate(index, 'url', e.target.value, false)}
              onBlur={(e) => onUpdate(index, 'url', e.target.value, true)}
              placeholder="Enter URL..."
            />
            
            <input
              type="text"
              placeholder="Link Label"
              className="input input-sm input-bordered w-full"
              value={link.title}
              onChange={(e) => onUpdate(index, 'title', e.target.value, false)}
              onBlur={(e) => onUpdate(index, 'title', e.target.value, true)}
            />
            
            <div className="flex gap-2 items-center justify-between">
              <div className="flex gap-2 items-center">
                <IconSelector
                  value={link.icon}
                  onChange={(value) => onUpdate(index, 'icon', value)}
                />
                <BadgeSelector
                  value={link.badge?.type || 'none'}
                  text={link.badge?.text || ''}
                  onChangeType={(type, text) => {
                    const updates = {
                      badge_type: type === 'none' ? null : type,
                      badge_text: text || null,
                      badge: {
                        type,
                        text: text || ''
                      }
                    };
                    onUpdate(index, 'badge', updates);
                  }}
                  onChangeText={(value) => onUpdate(index, 'badge.text', value, false)}
                  onBlurText={(value) => onUpdate(index, 'badge.text', value, true)}
                />
              </div>
              <button
                className="btn btn-ghost btn-sm text-error"
                onClick={() => onDelete(index)}
              >
                <FaTrash />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const THEME_OPTIONS = {
  'light': { label: 'Light', colors: ['#A0AEC0', '#F56565', '#48BB78', '#1A202C'] },
  'dark': { label: 'Dark', colors: ['#6B46C1', '#F56565', '#48BB78', '#1A202C'] },
  'cupcake': { label: 'Cupcake', colors: ['#65FCE0', '#FFB7C5', '#FFA07A', '#1A202C'] },
  'bumblebee': { label: 'Bumblebee', colors: ['#F6E05E', '#F6E05E', '#1A202C', '#1A202C'] },
  'emerald': { label: 'Emerald', colors: ['#10B981', '#60A5FA', '#F56565', '#1A202C'] },
  'corporate': { label: 'Corporate', colors: ['#60A5FA', '#A0AEC0', '#A0AEC0', '#1A202C'] },
  'synthwave': { label: 'Synthwave', colors: ['#E779C1', '#60A5FA', '#F6AD55', '#1A202C'] },
  'retro': { label: 'Retro', colors: ['#FFB4A2', '#31C48D', '#F6AD55', '#1A202C'] },
  'cyberpunk': { label: 'Cyberpunk', colors: ['#FF1A75', '#0CE7FA', '#A0AEC0', '#1A202C'] },
  'valentine': { label: 'Valentine', colors: ['#E779C1', '#A0AEC0', '#A0AEC0', '#1A202C'] },
  'halloween': { label: 'Halloween', colors: ['#F6AD55', '#9F7AEA', '#31C48D', '#1A202C'] },
  'garden': { label: 'Garden', colors: ['#FF1A75', '#A0AEC0', '#31C48D', '#1A202C'] },
  'forest': { label: 'Forest', colors: ['#31C48D', '#31C48D', '#31C48D', '#1A202C'] },
  'aqua': { label: 'Aqua', colors: ['#0CE7FA', '#9F7AEA', '#F6E05E', '#1A202C'] },
  'lofi': { label: 'Lo-Fi', colors: ['#1A202C', '#A0AEC0', '#A0AEC0', '#1A202C'] },
  'pastel': { label: 'Pastel', colors: ['#9F7AEA', '#FFB4A2', '#31C48D', '#1A202C'] },
  'fantasy': { label: 'Fantasy', colors: ['#6B46C1', '#60A5FA', '#F6AD55', '#1A202C'] },
  'wireframe': { label: 'Wireframe', colors: ['#A0AEC0', '#A0AEC0', '#A0AEC0', '#A0AEC0'] },
  'black': { label: 'Black', colors: ['#1A202C', '#1A202C', '#1A202C', '#1A202C'] },
  'luxury': { label: 'Luxury', colors: ['#FFFFFF', '#1A202C', '#9F7AEA', '#F6AD55'] },
  'dracula': { label: 'Dracula', colors: ['#FF79C6', '#A0AEC0', '#F6AD55', '#1A202C'] },
  'cmyk': { label: 'CMYK', colors: ['#0CE7FA', '#FF1A75', '#F6AD55', '#1A202C'] },
  'autumn': { label: 'Autumn', colors: ['#FF1A75', '#FF1A75', '#F6AD55', '#1A202C'] },
  'business': { label: 'Business', colors: ['#60A5FA', '#A0AEC0', '#FF1A75', '#1A202C'] },
  'acid': { label: 'Acid', colors: ['#FF1A75', '#F6AD55', '#31C48D', '#1A202C'] },
  'lemonade': { label: 'Lemonade', colors: ['#31C48D', '#31C48D', '#31C48D', '#1A202C'] },
  'night': { label: 'Night', colors: ['#0CE7FA', '#9F7AEA', '#FF1A75', '#1A202C'] },
  'coffee': { label: 'Coffee', colors: ['#F6AD55', '#31C48D', '#31C48D', '#1A202C'] },
  'winter': { label: 'Winter', colors: ['#60A5FA', '#A0AEC0', '#FF1A75', '#1A202C'] },
  'dim': { label: 'Dim', colors: ['#31C48D', '#FF1A75', '#9F7AEA', '#1A202C'] },
  'nord': { label: 'Nord', colors: ['#A0AEC0', '#A0AEC0', '#A0AEC0', '#1A202C'] },
  'sunset': { label: 'Sunset', colors: ['#FF79C6', '#9F7AEA', '#9F7AEA', '#1A202C'] },
  'caramellatte': { label: 'Caramellatte', colors: ['#1A202C', '#1A202C', '#1A202C', '#1A202C'] },
  'abyss': { label: 'Abyss', colors: ['#31C48D', '#9F7AEA', '#A0AEC0', '#1A202C'] },
  'silk': { label: 'Silk', colors: ['#F6E05E', '#FF79C6', '#31C48D', '#1A202C'] }
};

const ThemeSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedTheme = THEME_OPTIONS[value] || THEME_OPTIONS['light'];

  const ThemePreview = ({ colors, size = 'normal' }) => (
    <div className="flex gap-0.5">
      {colors.map((color, i) => (
        <div
          key={i}
          className={`${size === 'small' ? 'w-3 h-3' : 'w-5 h-5'} rounded-sm`}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );

  return (
    <div className="relative w-full">
      <button
        type="button"
        className="select select-bordered w-full flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <ThemePreview colors={selectedTheme.colors} size="small" />
          <span>{selectedTheme.label}</span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-base-100 shadow-xl rounded-box border border-base-300 p-2">
          <div className="grid grid-cols-2 gap-1 max-h-[300px] overflow-y-auto">
            {Object.entries(THEME_OPTIONS).map(([key, { colors, label }]) => (
              <button
                key={key}
                className={`btn btn-ghost justify-start h-12 min-h-0 ${value === key ? 'bg-base-200' : ''}`}
                onClick={() => {
                  onChange(key);
                  setIsOpen(false);
                }}
              >
                <div className="flex flex-col items-start gap-1">
                  <ThemePreview colors={colors} />
                  <span className="text-sm">{label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const detectIconFromUrl = (url) => {
  try {
    const urlLower = url.toLowerCase();
    
    // Extract domain using regex
    const domainMatch = urlLower.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?]+)/i);
    const domain = domainMatch ? domainMatch[1] : '';
    
    // Social Media
    if (/(?:^|\.)(?:x|twitter)\.com$/.test(domain)) return 'x';
    if (/(?:^|\.)(?:facebook|fb)\.com$/.test(domain)) return 'facebook';
    if (/(?:^|\.)instagram\.com$/.test(domain)) return 'instagram';
    if (/(?:^|\.)(?:youtube\.com|youtu\.be)$/.test(domain)) return 'youtube';
    if (/(?:^|\.)tiktok\.com$/.test(domain)) return 'tiktok';
    if (/(?:^|\.)threads\.net$/.test(domain)) return 'threads';
    
    // Professional
    if (/(?:^|\.)linkedin\.com$/.test(domain)) return 'linkedin';
    if (/(?:^|\.)github\.com$/.test(domain)) return 'github';
    if (/(?:^|\.)stackoverflow\.com$/.test(domain)) return 'stackoverflow';
    
    // Gaming & Community
    if (/(?:^|\.)twitch\.tv$/.test(domain)) return 'twitch';
    if (/(?:^|\.)(?:discord\.com|discord\.gg)$/.test(domain)) return 'discord';
    if (/(?:^|\.)reddit\.com$/.test(domain)) return 'reddit';
    
    // Messaging
    if (/(?:^|\.)whatsapp\.com$/.test(domain)) return 'whatsapp';
    if (/(?:^|\.)(?:t\.me|telegram\.org)$/.test(domain)) return 'telegram';
    if (/(?:^|\.)snapchat\.com$/.test(domain)) return 'snapchat';
    
    // Creative
    if (/(?:^|\.)pinterest\.com$/.test(domain)) return 'pinterest';
    if (/(?:^|\.)dribbble\.com$/.test(domain)) return 'dribbble';
    if (/(?:^|\.)behance\.net$/.test(domain)) return 'behance';
    if (/(?:^|\.)deviantart\.com$/.test(domain)) return 'deviantart';
    if (/(?:^|\.)vimeo\.com$/.test(domain)) return 'vimeo';
    
    // Content & Support
    if (/(?:^|\.)medium\.com$/.test(domain)) return 'medium';
    if (/(?:^|\.)substack\.com$/.test(domain)) return 'substack';
    if (/(?:^|\.)patreon\.com$/.test(domain)) return 'patreon';
    if (/(?:^|\.)ko-fi\.com$/.test(domain)) return 'kofi';
    if (/(?:^|\.)buymeacoffee\.com$/.test(domain)) return 'buymeacoffee';
    if (/(?:^|\.)spotify\.com$/.test(domain)) return 'spotify';
    if (/(?:^|\.)soundcloud\.com$/.test(domain)) return 'soundcloud';
    
    // Default to globe icon for other websites
    return 'globe';
  } catch (e) {
    console.log(e);
    // If regex matching fails, return default link icon
    return 'link';
  }
};

const DashboardPage = () => {
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [config, setConfig] = useState({
    theme: 'dark',
    position: 'bottom-left',
    title: 'Quick Links',
    layout: 'horizontal',
    links: []
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [deletingLinkIndex, setDeletingLinkIndex] = useState(null);
  
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getPositionClasses = (position) => {
    switch (position) {
      case 'top-right':
        return 'items-start justify-end pt-5 pr-4';
      case 'top-left':
        return 'items-start justify-start pt-5 pl-4';
      case 'top-center':
        return 'items-start justify-center pt-5';
      case 'bottom-right':
        return 'items-end justify-end pb-5 pr-4';
      case 'bottom-left':
        return 'items-end justify-start pb-5 pl-4';
      case 'bottom-center':
        return 'items-end justify-center pb-5';
      case 'center-right':
        return 'items-center justify-end pr-4';
      case 'center-left':
        return 'items-center justify-start pl-4';
      default:
        return 'items-start justify-end pt-5 pr-4'; // default to top-right
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log('Loading config...');
        const config = await loadConfig();
        console.log('Loaded config:', config);
        
        if (!config) {
          throw new Error('Failed to load configuration');
        }

        console.log('Fetching user data...');
        const response = await fetch(`${config.api.baseUrl}functions/v1/twitch-auth/validate`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            ...config.api.cors.headers,
            'Origin': config.api.cors.origin
          }
        });
        console.log('User data response:', response.status, response.statusText);

        if (!response.ok) {
          const data = await response.json();
          if (data.requiresNewAuth) {
            console.log('Token requires re-authentication');
            navigate('/');
            return;
          }
          throw new Error(data.error || 'Failed to fetch user data');
        }

        const data = await response.json();
        console.log('User data received:', data);
        setUserData(data);

        // Fetch user settings
        console.log('Fetching user settings...');
        const settingsResponse = await fetch(`${config.api.baseUrl}functions/v1/user-settings`, {
          credentials: 'include',
          headers: {
            ...config.api.cors.headers,
            'Origin': config.api.cors.origin
          }
        });

        let settings = { theme: 'dark', position: 'bottom-left', title: 'Quick Links' };
        
        if (settingsResponse.ok) {
          const responseData = await settingsResponse.json();
          console.log('Settings received:', responseData);
          if (responseData && typeof responseData === 'object') {
            settings = {
              theme: responseData.theme || settings.theme,
              position: responseData.position || settings.position,
              title: responseData.title || settings.title
            };
          }
        } else {
          console.warn('Failed to fetch settings:', await settingsResponse.text());
        }

        setConfig(prev => ({
          ...prev,
          ...settings
        }));

        // Continue with fetching links
        console.log('Fetching links...');
        const linksResponse = await fetch(`${config.api.baseUrl}functions/v1/twitch-links`, {
          credentials: 'include',
          headers: {
            ...config.api.cors.headers,
            'Origin': config.api.cors.origin
          }
        });
        console.log('Links response:', linksResponse.status, linksResponse.statusText);

        if (!linksResponse.ok) {
          console.warn('Failed to fetch links:', await linksResponse.text());
          setConfig(prev => ({
            ...prev,
            links: []
          }));
        } else {
          const links = await linksResponse.json();
          console.log('Links received:', links);
          
          // Transform and sort the links data
          const transformedLinks = links
            .map(link => ({
              ...link,
              badge: {
                type: link.badge_type || 'none',
                text: link.badge_text || ''
              }
            }))
            .sort((a, b) => a.position - b.position);

          setConfig(prev => ({
            ...prev,
            links: transformedLinks || []
          }));
        }
      } catch (err) {
        console.error('Error in fetchUserData:', err);
        setError(err.message);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    console.log('DashboardPage mounted, starting data fetch...');
    fetchUserData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      const config = await loadConfig();
      if (!config) {
        throw new Error('Failed to load configuration');
      }

      const response = await fetch(`${config.api.baseUrl}functions/v1/twitch-auth/revoke`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...config.api.cors.headers,
          'Origin': config.api.cors.origin
        }
      });

      if (!response.ok) {
        throw new Error('Failed to logout');
      }

      // Clear the session cookie by setting it to expire
      document.cookie = 'xstreamlink_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      // Clear any local storage tokens if they exist
      localStorage.removeItem('cliplinker_token');
      sessionStorage.removeItem('cliplinker_token');

      navigate('/');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleNewLinkClick = () => {
    setIsModalOpen(true);
  };

  const handleCreateLink = async () => {
    if (newLinkUrl && !isCreatingLink) {
      try {
        setIsCreatingLink(true);
        // Get the highest position value
        const maxPosition = config.links.reduce((max, link) => 
          Math.max(max, link.position || 0), -1);

        const newLink = {
          url: newLinkUrl,
          title: '',
          icon: detectIconFromUrl(newLinkUrl),
          badge_type: null,
          badge_text: null,
          position: maxPosition + 1
        };

        // Save to database immediately
        const configData = await loadConfig();
        if (!configData) {
          throw new Error('Failed to load configuration');
        }

        const response = await fetch(`${configData.api.baseUrl}functions/v1/twitch-links`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...configData.api.cors.headers,
            'Content-Type': 'application/json',
            'Origin': configData.api.cors.origin
          },
          body: JSON.stringify(newLink)
        });

        if (!response.ok) {
          throw new Error('Failed to save link');
        }

        const savedLink = await response.json();
        
        // Update local state with the saved link
        setConfig(prev => ({
          ...prev,
          links: [...prev.links, {
            ...savedLink,
            badge: {
              type: savedLink.badge_type || 'none',
              text: savedLink.badge_text || ''
            }
          }].sort((a, b) => a.position - b.position)
        }));
        
        setNewLinkUrl('');
        setIsModalOpen(false);
      } catch (err) {
        console.error('Error saving link:', err);
        setError(err.message);
      } finally {
        setIsCreatingLink(false);
      }
    }
  };

  const handleLinkDelete = async (index) => {
    try {
      const linkToDelete = config.links[index];
      
      // Delete from database immediately
      const configData = await loadConfig();
      if (!configData) {
        throw new Error('Failed to load configuration');
      }

      const response = await fetch(`${configData.api.baseUrl}functions/v1/twitch-links/${linkToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          ...configData.api.cors.headers,
          'Origin': configData.api.cors.origin
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete link');
      }

      // Update local state
      setConfig(prev => ({
        ...prev,
        links: prev.links.filter((_, i) => i !== index)
      }));
    } catch (err) {
      console.error('Error deleting link:', err);
      setError(err.message);
    }
  };

  const handleLinkUpdate = async (index, field, value, updateServer = true) => {
    try {
      const updatedLinks = config.links.map((link, i) => {
        if (i === index) {
          if (field === 'badge') {
            return {
              ...link,
              ...value
            };
          }
          if (field === 'badge.type' || field === 'badge.text') {
            const [, badgeField] = field.split('.');
            return {
              ...link,
              [`badge_${badgeField}`]: value === 'none' ? null : value,
              badge: {
                ...link.badge,
                [badgeField]: value
              }
            };
          }
          return { ...link, [field]: value };
        }
        return link;
      });

      // Update local state immediately
      setConfig(prev => ({
        ...prev,
        links: updatedLinks
      }));

      // Only send to server if updateServer is true
      if (updateServer) {
        const updatedLink = updatedLinks[index];
        
        // Create a clean object for the server without frontend-only properties
        const linkToSave = {
          id: updatedLink.id,
          title: updatedLink.title,
          url: updatedLink.url,
          icon: updatedLink.icon,
          position: updatedLink.position,
          badge_type: updatedLink.badge?.type === 'none' ? null : updatedLink.badge?.type,
          badge_text: updatedLink.badge?.text || null
        };

        const configData = await loadConfig();
        if (!configData) {
          throw new Error('Failed to load configuration');
        }

        const response = await fetch(`${configData.api.baseUrl}functions/v1/twitch-links/${updatedLink.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            ...configData.api.cors.headers,
            'Content-Type': 'application/json',
            'Origin': configData.api.cors.origin
          },
          body: JSON.stringify(linkToSave)
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Server error:', errorData);
          throw new Error(errorData.error || 'Failed to update link');
        }
      }
    } catch (err) {
      console.error('Error updating link:', err);
      setError(err.message);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = config.links.findIndex(link => link.id.toString() === active.id);
    const newIndex = config.links.findIndex(link => link.id.toString() === over.id);
    
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update positions for all affected items
    const updatedItems = arrayMove(config.links, oldIndex, newIndex)
      .map((item, index) => ({
        ...item,
        position: index
      }));

    // Update local state immediately
    setConfig(prev => ({
      ...prev,
      links: updatedItems
    }));

    try {
      // Update positions in the database
      const configData = await loadConfig();
      if (!configData) {
        throw new Error('Failed to load configuration');
      }

      // Update each affected link's position
      await Promise.all(updatedItems.map(async (link) => {
        // Create a clean object for the server without frontend-only properties
        const linkToSave = {
          id: link.id,
          title: link.title,
          url: link.url,
          icon: link.icon,
          position: link.position,
          badge_type: link.badge?.type === 'none' ? null : link.badge?.type,
          badge_text: link.badge?.text || null
        };

        const response = await fetch(`${configData.api.baseUrl}functions/v1/twitch-links/${link.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            ...configData.api.cors.headers,
            'Content-Type': 'application/json',
            'Origin': configData.api.cors.origin
          },
          body: JSON.stringify(linkToSave)
        });

        if (!response.ok) {
          throw new Error('Failed to update link position');
        }
      }));
    } catch (err) {
      console.error('Error updating positions:', err);
      setError(err.message);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      const configData = await loadConfig();
      if (!configData) {
        throw new Error('Failed to load configuration');
      }

      const settingsToSave = {
        theme: config.theme,
        position: config.position,
        title: config.title
      };

      console.log('Saving settings:', settingsToSave);

      const response = await fetch(`${configData.api.baseUrl}functions/v1/user-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          ...configData.api.cors.headers,
          'Content-Type': 'application/json',
          'Origin': configData.api.cors.origin
        },
        body: JSON.stringify(settingsToSave)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to save settings:', errorText);
        throw new Error('Failed to save settings: ' + errorText);
      }

      const savedSettings = await response.json();
      console.log('Settings saved successfully:', savedSettings);

      // Update local state with the saved settings
      setConfig(prev => ({
        ...prev,
        theme: savedSettings.theme || prev.theme,
        position: savedSettings.position || prev.position,
        title: savedSettings.title || prev.title
      }));

    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      {/* Navigation Bar */}
      <div className="navbar bg-base-100">
        <div className="flex-1">
          <a className="btn btn-ghost text-xl">XStreamLink</a>
        </div>
        <div className="flex-none gap-2">
          <button 
            className="btn btn-accent btn-sm gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 border-0 text-white hover:from-violet-600 hover:to-fuchsia-600"
            onClick={() => navigate('/subscriptions')}
          >
            <FaCrown className="text-yellow-300" />
            Upgrade Pro
          </button>
          <div className="dropdown dropdown-end">
            <div tabIndex={0} role="button" className="btn btn-ghost btn-circle avatar">
              <div className="w-10 rounded-full">
                <img 
                  src={userData?.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/13e5fa74-defa-11e9-809c-784f43822e80-profile_image-300x300.png'} 
                  alt="profile" 
                />
              </div>
            </div>
            <ul tabIndex={0} className="mt-3 z-[1] p-2 shadow menu menu-sm dropdown-content bg-base-100 rounded-box w-52">
              <li>
                <a className="justify-between">
                  Profile
                  <span className="badge badge-primary">Twitch</span>
                </a>
              </li>
              <li><a>Settings</a></li>
              <li><a onClick={handleLogout}>Logout</a></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Welcome, {userData?.display_name}!</h1>
        
        {/* Configuration Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Links Management */}
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex justify-between items-center mb-4">
                <h2 className="card-title">Links</h2>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={handleNewLinkClick}
                >
                  New Link
                </button>
              </div>
              
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={config.links.map(link => link.id.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {config.links.map((link, index) => (
                      <SortableLink
                        key={link.id}
                        link={link}
                        index={index}
                        onUpdate={handleLinkUpdate}
                        onDelete={() => setDeletingLinkIndex(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>

          {/* Preview and Settings */}
          <div className="space-y-6">
            {/* Preview Panel */}
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <div className="flex items-center gap-2">
                  <h2 className="card-title">Preview</h2>
                  <span className="text-xs opacity-60">(not to scale)</span>
                </div>
                <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                  <div data-theme={config.theme} className="absolute top-0 left-0 w-full h-full preview-screen-container rounded-lg overflow-hidden">
                    <div className={`w-full h-full flex ${getPositionClasses(config.position)}`}>
                      <style>
                        {`
                          .preview-container {
                            background: hsl(var(--p)) !important;
                            transition: background 0.2s ease-in-out;
                            position: relative;
                          }
                          .preview-container:hover {
                            background: hsl(var(--p)) !important;
                            backdrop-filter: blur(4px);
                          }
                          .preview-container button.inline-flex {
                            background: oklch(var(--b1)) !important;
                            color: oklch(var(--bc));
                          }
                          .preview-container button.inline-flex:hover {
                            background: oklch(var(--b2)) !important;
                          }
                          .preview-scroll-wrapper {
                            width: 100%;
                            overflow-x: ${config.layout === 'horizontal' ? 'hidden' : 'auto'};
                            overflow-y: ${config.layout === 'horizontal' ? 'hidden' : 'auto'};
                            margin-bottom: 16px;
                            max-height: ${config.layout === 'vertical' ? '200px' : 'none'};
                          }
                          .preview-container:hover .preview-scroll-wrapper {
                            overflow-x: ${config.layout === 'horizontal' ? 'auto' : 'auto'};
                            overflow-y: ${config.layout === 'horizontal' ? 'hidden' : 'auto'};
                          }
                          .preview-scroll {
                            position: relative;
                            width: ${config.layout === 'horizontal' ? 'max-content' : '100%'};
                          }
                          .preview-scroll-wrapper::-webkit-scrollbar {
                            ${config.layout === 'horizontal' ? 'height' : 'width'}: 6px;
                          }
                          .preview-scroll-wrapper::-webkit-scrollbar-track {
                            background: rgba(0, 0, 0, 0.1);
                            border-radius: 3px;
                          }
                          .preview-scroll-wrapper::-webkit-scrollbar-thumb {
                            background: rgba(255, 255, 255, 0.3);
                            border-radius: 3px;
                          }
                          .preview-scroll-wrapper::-webkit-scrollbar-thumb:hover {
                            background: rgba(255, 255, 255, 0.4);
                          }
                          .preview-container {
                            margin: 0.5rem 1rem;
                            max-width: 90%;
                            overflow: visible !important;
                          }
                          .preview-container .card-body {
                            overflow: visible;
                            padding: 0.125rem 0.25rem;
                          }
                          .promo-link {
                            position: absolute;
                            right: ${config.position.endsWith('right') ? '8px' : 'auto'};
                            left: ${config.position.endsWith('right') ? 'auto' : '8px'};
                            bottom: 4px;
                            font-size: 8px;
                            color: rgba(192, 192, 192, 0.66);
                            white-space: nowrap;
                            transition: opacity 0.2s ease-in-out;
                            line-height: 1;
                            z-index: 60;
                          }
                          .promo-link:hover {
                            opacity: 1;
                          }
                        `}
                      </style>
                      <div className="preview-container card" style={{ margin: '1rem', maxWidth: '90%', overflow: 'visible' }}>
                        <div className="card-body p-1">
                          <div className="preview-scroll-wrapper">
                            <div className="preview-scroll">
                              <div className={`flex ${config.layout === 'horizontal' ? 'flex-row' : 'flex-col'} gap-2`}>
                                {config.links.map((link, index) => (
                                  <div key={index} className="relative">
                                    <button
                                      className="inline-flex text-base-content transition-colors max-w-[206.25px] z-50 rounded-lg"
                                      style={{
                                        padding: link.title ? '0.25rem 0.5rem' : '0.375rem',
                                        alignItems: 'start',
                                        width: 'fit-content',
                                        height: 'fit-content',
                                        fontSize: '0.875rem',
                                        lineHeight: '1.25rem',
                                        minHeight: 'unset',
                                        backgroundColor: 'oklch(var(--b1))',
                                        color: 'oklch(var(--bc))'
                                      }}
                                    >
                                      <div 
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns: link.title ? 'auto 1fr auto' : 'auto',
                                          gap: '0.5rem',
                                          alignItems: 'center',
                                          width: '100%',
                                          minWidth: 0
                                        }}
                                      >
                                        {React.createElement(ICON_OPTIONS[link.icon || 'link'].icon, {
                                          className: "shrink-0"
                                        })}
                                        {link.title && (
                                          <span className="line-clamp-3 text-left text-sm break-words overflow-hidden leading-tight">
                                            {link.title}
                                          </span>
                                        )}
                                        {link.badge?.type !== 'none' && link.badge?.text && link.title && (
                                          <span 
                                            className={`badge badge-sm text-xs shrink-0 ${BADGE_OPTIONS[link.badge.type]?.class || ''}`}
                                          >
                                            {link.badge.text.slice(0, 5)}
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          {config.links.length > 0 && (
                            <a 
                              href="https://dashboard.twitch.tv/extensions/kzi9g3hros2zj24woyavz76g535029-0.0.1"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="promo-link"
                            >
                              xstreamlink.com
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* General Settings */}
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">General Settings</h2>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Layout</span>
                  </label>
                  <select 
                    className="select select-bordered w-full"
                    value={config.layout}
                    onChange={async (e) => {
                      const newLayout = e.target.value;
                      setConfig(prev => ({ ...prev, layout: newLayout }));
                      try {
                        const configData = await loadConfig();
                        const response = await fetch(`${configData.api.baseUrl}functions/v1/user-settings`, {
                          method: 'PUT',
                          credentials: 'include',
                          headers: {
                            ...configData.api.cors.headers,
                            'Content-Type': 'application/json',
                            'Origin': configData.api.cors.origin
                          },
                          body: JSON.stringify({
                            theme: config.theme,
                            position: config.position,
                            title: config.title,
                            layout: newLayout
                          })
                        });

                        if (!response.ok) {
                          throw new Error('Failed to save layout setting');
                        }
                      } catch (err) {
                        console.error('Error saving layout:', err);
                        setError(err.message);
                      }
                    }}
                  >
                    <option value="horizontal">Horizontal</option>
                    <option value="vertical">Vertical</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Preview Theme</span>
                    <span className="label-text-alt text-xs opacity-60">Only affects the preview panel</span>
                  </label>
                  <ThemeSelector 
                    value={config.theme}
                    onChange={async (value) => {
                      setConfig(prev => ({ ...prev, theme: value }));
                      try {
                        const configData = await loadConfig();
                        const response = await fetch(`${configData.api.baseUrl}functions/v1/user-settings`, {
                          method: 'PUT',
                          credentials: 'include',
                          headers: {
                            ...configData.api.cors.headers,
                            'Content-Type': 'application/json',
                            'Origin': configData.api.cors.origin
                          },
                          body: JSON.stringify({
                            theme: value,
                            position: config.position,
                            title: config.title
                          })
                        });

                        if (!response.ok) {
                          throw new Error('Failed to save theme setting');
                        }
                      } catch (err) {
                        console.error('Error saving theme:', err);
                        setError(err.message);
                      }
                    }}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Position</span>
                  </label>
                  <select 
                    className="select select-bordered w-full"
                    value={config.position}
                    onChange={async (e) => {
                      const newPosition = e.target.value;
                      setConfig(prev => ({ ...prev, position: newPosition }));
                      try {
                        const configData = await loadConfig();
                        const response = await fetch(`${configData.api.baseUrl}functions/v1/user-settings`, {
                          method: 'PUT',
                          credentials: 'include',
                          headers: {
                            ...configData.api.cors.headers,
                            'Content-Type': 'application/json',
                            'Origin': configData.api.cors.origin
                          },
                          body: JSON.stringify({
                            theme: config.theme,
                            position: newPosition,
                            title: config.title
                          })
                        });

                        if (!response.ok) {
                          throw new Error('Failed to save position setting');
                        }
                      } catch (err) {
                        console.error('Error saving position:', err);
                        setError(err.message);
                      }
                    }}
                  >
                    <option value="top-left">Top Left</option>
                    <option value="top-center">Top Center</option>
                    <option value="top-right">Top Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="bottom-center">Bottom Center</option>
                    <option value="bottom-right">Bottom Right</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Title</span>
                  </label>
                  <input 
                    type="text" 
                    className="input input-bordered w-full"
                    value={config.title}
                    onChange={(e) => setConfig(prev => ({ ...prev, title: e.target.value }))}
                    onBlur={async (e) => {
                      const newTitle = e.target.value;
                      try {
                        const configData = await loadConfig();
                        const response = await fetch(`${configData.api.baseUrl}functions/v1/user-settings`, {
                          method: 'PUT',
                          credentials: 'include',
                          headers: {
                            ...configData.api.cors.headers,
                            'Content-Type': 'application/json',
                            'Origin': configData.api.cors.origin
                          },
                          body: JSON.stringify({
                            theme: config.theme,
                            position: config.position,
                            title: newTitle
                          })
                        });

                        if (!response.ok) {
                          throw new Error('Failed to save title setting');
                        }
                      } catch (err) {
                        console.error('Error saving title:', err);
                        setError(err.message);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Link Modal */}
      {isModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Add New Link</h3>
            <div className="form-control">
              <input
                type="url"
                placeholder="Enter URL"
                className="input input-bordered w-full"
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                disabled={isCreatingLink}
              />
            </div>
            <div className="modal-action">
              <button 
                className="btn btn-ghost"
                onClick={() => {
                  setIsModalOpen(false);
                  setNewLinkUrl('');
                }}
                disabled={isCreatingLink}
              >
                Cancel
              </button>
              <button 
                className={`btn btn-primary ${isCreatingLink ? 'loading' : ''}`}
                onClick={handleCreateLink}
                disabled={!newLinkUrl || isCreatingLink}
              >
                {isCreatingLink ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
          <div 
            className="modal-backdrop"
            onClick={() => {
              if (!isCreatingLink) {
                setIsModalOpen(false);
                setNewLinkUrl('');
              }
            }}
          ></div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingLinkIndex !== null && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Delete Link</h3>
            <p>Are you sure you want to delete this link?</p>
            <div className="modal-action">
              <button 
                className="btn btn-ghost"
                onClick={() => setDeletingLinkIndex(null)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-error"
                onClick={() => {
                  handleLinkDelete(deletingLinkIndex);
                  setDeletingLinkIndex(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
          <div 
            className="modal-backdrop"
            onClick={() => setDeletingLinkIndex(null)}
          ></div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 